/**
 * @fileoverview `buildContactOutcomeAdapter` — the ONE adapter from an axios contact client to
 * the MCR-001 `{success, outcome, data, retryAfter}` envelope `createUseContactForm` consumes.
 * @package @puffin-core/marketing-shell
 *
 * ## Why this is shared, and what it replaces
 *
 * AAS-I18N-002 ruling 42 (Q-W3W4-1, 2026-08-10). This function existed **nine times**: inline in
 * the marketing `ContactPage` of palm-web, payday-web, planner-web, patrol-web, port-web,
 * prep-web and prospect-web, plus an already-extracted local copy in purchase-web
 * (`shared/services/contactOutcomeAdapter.js`) that the storefront CMS block and its marketing
 * page shared. Nine copies of one status→outcome mapping is the rule-of-three violation the
 * factory-first standard exists to prevent, and — as below — the bug they all carried had to be
 * fixed in one place or nine.
 *
 * ## 🔴 The defect every copy carried
 *
 * The MCR-001 contact route is guarded by **two** rate limiters, deliberately:
 *
 *   - `marketing_contact_submission` — per-IP  (`createSubmissionGuard`)
 *   - `marketing_contact_email`      — per-email (`createEmailRateLimiter`)
 *
 * Both answer **429**, and every marketing catalog in the fleet carries **two** translated
 * sentences for them — `contact.errors.rate_limited` and `contact.errors.rate_limited_email`.
 * But every copy of the adapter read:
 *
 * ```js
 * if (status === 429) outcome = 'rate_limited';
 * else if (remoteOutcome && ...) outcome = remoteOutcome;   // ← unreachable on a 429
 * ```
 *
 * The 429 arm **short-circuits before the body is ever consulted**, so
 * `contact.errors.rate_limited_email` could not render on any surface in any app. It is a fully
 * translated, `_meta`-tracked, parity-green leaf that **nothing can reach** — a census scores it
 * 0 findings and every test stays green, because the failure is a missing BRANCH, not a missing
 * string. That is the same shape as RECIPE miss-class 5, arrived at from the opposite direction.
 *
 * ## How it is fixed, and why not by reordering the branches
 *
 * Simply consulting `remoteOutcome` first would make the outcome depend on server PROSE shape and
 * on whichever body key (`error` vs `outcome`) a given route happens to set — the matching style
 * §11.6 forbids, and the one that was measured **100 % dead** elsewhere in this initiative because
 * the messages had changed underneath it.
 *
 * Instead the limiter is read as a **machine token**: `createRateLimiter`'s bounded `name` rides
 * out on the 429 as `error.details.limiter` (Q-R39-1), which is the same label already used as the
 * `rate_limit_exceeded_total` `surface` metric attribute — one vocabulary, two consumers. The
 * canonical `readRateLimiterToken` from `@puffin-core/error-handling` reads it off `errorDetails`
 * — the carrier `ApiError` hoists — so callers cannot invent a subtly different accessor.
 *
 * An unknown or absent token degrades to the generic `rate_limited`, which is exactly today's
 * behaviour — so this can only ever ADD a distinction, never remove one.
 */

import { readRateLimiterToken } from '@puffin-core/error-handling';

/**
 * MCR-001 limiter token → contact outcome.
 *
 * Deliberately small and closed: these are the only two limiters mounted on the contact route
 * (`createSubmissionGuard` and `createEmailRateLimiter`). A third limiter added server-side
 * without a matching entry here degrades to `rate_limited` rather than breaking.
 *
 * @constant {Readonly<Record<string, string>>}
 */
export const CONTACT_RATE_LIMIT_OUTCOMES = Object.freeze({
  marketing_contact_submission: 'rate_limited',
  marketing_contact_email: 'rate_limited_email',
});

/** The outcome used when a 429 names no limiter we know. @constant {string} */
export const GENERIC_RATE_LIMITED_OUTCOME = 'rate_limited';

/**
 * Resolve a failed contact submission to an MCR-001 outcome string.
 *
 * Exported separately from the adapter so it can be unit-tested against error shapes directly,
 * without standing up a client.
 *
 * @param {*} err - The rejection from `client.submitContact`.
 * @returns {string} An MCR-001 outcome enum value.
 */
export function resolveContactOutcome(err) {
  // 🔴 RES-CLIENT-001 `Q-N51` (R16, 2026-09-01): `ApiError` reads. `createContactApiClient` now
  // wraps its bespoke transport in a facade, so a rejection arrives normalized — status hoisted
  // to `err.status`, the server envelope kept whole at `err.body`, and `error.details` hoisted to
  // `err.errorDetails`. `err.response` does not exist on it, so the old reads would have gone
  // silently dead and every refusal would have resolved `persist_fail`.
  const status = err?.status;
  const body = err?.body;
  const remoteOutcome = body?.error?.code || body?.error || body?.outcome;

  if (status === 429) {
    const token = readRateLimiterToken(err);
    return (token && CONTACT_RATE_LIMIT_OUTCOMES[token]) || GENERIC_RATE_LIMITED_OUTCOME;
  }
  if (status === 422) return 'validation_fail';
  if (status === 403) return 'origin_blocked';
  if (typeof remoteOutcome === 'string' && remoteOutcome.length > 0) return remoteOutcome;
  return 'persist_fail';
}

/**
 * Adapt a contact API client to the envelope `createUseContactForm` expects.
 *
 * @param {{submitContact: (values: Object) => Promise<Object>}} client - Typically
 *   `createContactApiClient`'s result.
 * @param {string} apiPath - The endpoint the client posts to (carried through for diagnostics).
 * @returns {{submit: (values: Object) => Promise<Object>, apiPath: string}}
 */
export function buildContactOutcomeAdapter(client, apiPath) {
  return {
    async submit(values) {
      try {
        const data = await client.submitContact(values);
        return {
          success: true,
          outcome: 'success',
          data: { submissionId: data.submissionId || null, ...data },
        };
      } catch (err) {
        return {
          success: false,
          outcome: resolveContactOutcome(err),
          // From the ENVELOPE, not the header. Both contact limiters send
          // `error.details.retryAfter` alongside the `Retry-After` header
          // (`createEmailRateLimiter` / `createSubmissionGuard`), and `errorDetails` is the
          // canonical carrier `ApiError` hoists — headers are not preserved on it.
          retryAfter: err?.errorDetails?.retryAfter,
        };
      }
    },
    apiPath,
  };
}

export default buildContactOutcomeAdapter;
